import _styled from "styled-components";
import type { ComponentType } from "react";
import type { SanityRouteDocument } from "@ouster/shared";
import { useRouter } from "next/router";
import { clearRouterPath } from "~/features/app/utils";
import { BlogArticleCard } from "~/features/blog/article-card";
import { BlogArticlesGrid } from "~/features/blog/blog-articles-grid";
import { BlogFilters } from "~/features/blog/blog-filters";
import { BlogHero } from "~/features/blog/blog-hero";
import { BlogHighlightedArticles } from "~/features/blog/blog-highlighted-articles";
import { useBodyBackground } from "~/features/ui/use-body-background";
import { SanityPageBuilder, type SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import { BLOG_ARTICLES_PAGE_SIZE } from "./constants";
import type { SanityBlogPageData } from "./types";
type BlogRouteFields = Pick<SanityRouteDocument, "title" | "featuredArticle" | "highlightedArticlesHeadline" | "highlightedArticles">;
type PageBuilderComponent = ComponentType<{
  sections?: SanityPageBuilderRenderSection[];
}>;
type Props = {
  route: BlogRouteFields;
  /** The page's own page-builder sections, rendered between the two article batches. */
  sections: SanityPageBuilderRenderSection[];
  blogData: SanityBlogPageData;
  /** Swap the section renderer (the live layer injects the live-subscribing one). */
  PageBuilderComponent?: PageBuilderComponent;
};

/**
 * Renders the `/insights/blog` page body: newest article as hero,
 * category/industry filters, an infinite article grid (with the page-builder
 * sections in the middle), and curated highlighted articles.
 * Rendered by `SanityRouteView` in place of the plain hero + sections flow.
 */
export function SanityBlogPageView(props: Props) {
  const {
    route,
    sections,
    blogData,
    PageBuilderComponent = SanityPageBuilder
  } = props;
  useBodyBackground({
    mode: "light"
  });
  const router = useRouter();
  const activeCategory = typeof router.query.category === "string" ? router.query.category : "";
  const activeIndustry = typeof router.query.industry === "string" ? router.query.industry : "";
  function onChange(filterType: string, value: string) {
    // Drop the catch-all `uri` path param so it doesn't leak into the query string.
    const {
      uri: _uri,
      ...queryParams
    } = router.query;
    if (filterType === "Category") {
      if (value) queryParams.category = value;else delete queryParams.category;
    }
    if (filterType === "Industry") {
      if (value) queryParams.industry = value;else delete queryParams.industry;
    }
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: queryParams
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }
  function onClearFilters() {
    const {
      uri: _uri,
      category: _category,
      industry: _industry,
      ...rest
    } = router.query;
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: rest
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }
  return <>
      <_StyledH>{route.title || "Blog"}</_StyledH>
      {blogData.heroArticle && <BlogHero>
          <BlogArticleCard article={blogData.heroArticle} featured size="large" />
        </BlogHero>}
      <BlogFilters categories={blogData.categories} industries={blogData.industries} onChange={onChange} value={{
      category: activeCategory,
      industry: activeIndustry
    }} />
      <BlogArticlesGrid limit={BLOG_ARTICLES_PAGE_SIZE} total={blogData.total} initialArticles={blogData.initialArticles} featuredArticle={route.featuredArticle ?? undefined} middleBlocks={<PageBuilderComponent sections={sections} />} activeCategory={activeCategory} activeIndustry={activeIndustry} onClearFilters={onClearFilters} />
      <BlogHighlightedArticles headline={route.highlightedArticlesHeadline} articles={route.highlightedArticles} />
    </>;
}
var _StyledH = /*#__PURE__*/_styled("h1").withConfig({
  displayName: "blog___StyledH",
  componentId: "sc-1ks44n8-0"
})({
  "position": "absolute",
  "width": "1px",
  "height": "1px",
  "padding": "0",
  "margin": "-1px",
  "overflow": "hidden",
  "clip": "rect(0, 0, 0, 0)",
  "whiteSpace": "nowrap",
  "borderWidth": "0"
});